import React from 'react';
import { Link } from 'react-router-dom';
import type { MenuItem, Restaurant } from '../../services/restaurantService';
import MenuItemOptionsSheet from '../common/MenuItemOptionsSheet/MenuItemOptionsSheet';
import MenuLinePrompt from '../common/MenuItemOptionsSheet/MenuLinePrompt';
import MenuItemPriceBlock from '../common/MenuItemPriceBlock/MenuItemPriceBlock';
import { MenuCardAddToCart, useMenuItemCartControls } from '../../hooks/useMenuItemCartControls';
import {
  getDisplayMrp,
  getDisplayPrice,
  getEffectiveDisplayPrice,
  getStrikeThroughPrice,
  getMenuOptionsMeta,
} from '../../utils/menuPricing';
import { DEFAULT_IMAGES, getMenuItemImageUrl } from '../../constants/images';
import { resolveTag } from '../../utils/menuTags';
import './MenuBrowse.css';

interface MenuBrowseItemCardProps {
  item: MenuItem;
  restaurant: Restaurant;
  formatPrice: (price: number) => string;
  onAddToCart: MenuCardAddToCart;
  orderingEnabled?: boolean;
  /** Outlet "show menu item images" setting; when off the card has no image block. */
  showImages?: boolean;
}

const MenuBrowseItemCard: React.FC<MenuBrowseItemCardProps> = ({
  item,
  restaurant,
  formatPrice,
  onAddToCart,
  orderingEnabled = true,
  showImages = true,
}) => {
  const cart = useMenuItemCartControls(item, restaurant.appName, onAddToCart);
  const { quantity, selections } = cart;
  const optionsMeta = getMenuOptionsMeta(item);
  const isAvailable = item.availability !== false;
  const openOptionsSheet = cart.openSheet;
  const handleAdd = () => {
    if (!isAvailable) return;
    cart.handleIncrement();
  };

  const effectivePrice = getEffectiveDisplayPrice(item, { sizeTypeId: selections.sizeTypeId });
  const strikeThrough = getStrikeThroughPrice(item, { sizeTypeId: selections.sizeTypeId });
  const sellingPrice = getDisplayPrice(item, { sizeTypeId: selections.sizeTypeId });
  const displayMrp = getDisplayMrp(item, selections.sizeTypeId);
  const discountPercentage =
    strikeThrough && strikeThrough > effectivePrice
      ? Math.round(((strikeThrough - effectivePrice) / strikeThrough) * 100)
      : item.promotionalPercentOff != null && item.promotionalPercentOff > 0
        ? Math.round(item.promotionalPercentOff)
        : item.discountPercent != null && item.discountPercent > 0
          ? Math.round(item.discountPercent)
          : displayMrp && displayMrp > sellingPrice
            ? Math.round(((displayMrp - sellingPrice) / displayMrp) * 100)
            : item.discount != null && item.discount > 0
              ? Math.round(item.discount)
              : 0;

  const imageSrc =
    restaurant?.id && item.id && item.imageBig
      ? getMenuItemImageUrl(restaurant.id, item.id, item.imageBig)
      : DEFAULT_IMAGES.MENU_ITEM;

  const itemPath = `/restaurants/${restaurant.appName}/menu/${item.itemUrl || item.id}`;

  const priceBlock = (
    <MenuItemPriceBlock
      effectivePrice={effectivePrice}
      strikeThroughPrice={strikeThrough}
      discountPercentage={discountPercentage}
      sellingPrice={sellingPrice}
      formatPrice={formatPrice}
    />
  );

  const controls = !isAvailable ? (
    <div className="browse-item-card__oos-label">Out of Stock</div>
  ) : !orderingEnabled ? null : quantity === 0 ? (
    <button type="button" className="browse-item-card__add" onClick={handleAdd}>
      ADD
    </button>
  ) : (
    <div className="browse-item-card__qty">
      <button
        type="button"
        className="browse-item-card__qty-btn"
        onClick={cart.handleDecrement}
        aria-label="Decrease"
      >
        −
      </button>
      <span>{quantity}</span>
      <button
        type="button"
        className="browse-item-card__qty-btn"
        onClick={handleAdd}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );

  return (
    <>
      <article
        className={`browse-item-card${!isAvailable ? ' browse-item-card--oos' : ''}${
          !showImages ? ' browse-item-card--no-image' : ''
        }`}
      >
        {showImages && (
          <div className="browse-item-card__img-wrap">
            <Link to={itemPath}>
              <img
                src={imageSrc}
                alt={item.item}
                className="browse-item-card__img"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== DEFAULT_IMAGES.MENU_ITEM) {
                    target.src = DEFAULT_IMAGES.MENU_ITEM;
                  }
                }}
              />
            </Link>
            {controls}
          </div>
        )}
        <div className="browse-item-card__body">
          {showImages ? (
            <>
              {priceBlock}
              <Link to={itemPath} className="browse-item-card__name">
                {item.item}
              </Link>
            </>
          ) : (
            <div className="browse-item-card__head">
              <span
                className={`browse-item-card__veg-dot${item.itemType === 'nonveg' ? ' non-veg' : ''}`}
                aria-label={item.itemType === 'nonveg' ? 'Non-vegetarian' : 'Vegetarian'}
              />
              <Link to={itemPath} className="browse-item-card__name">
                {item.item}
              </Link>
            </div>
          )}
          {item.tags && item.tags.length > 0 && (() => {
            const rt = resolveTag(item.tags![0]);
            return (
              <span className="browse-item-card__tag-pill" style={{ background: rt.bg, color: rt.color, borderColor: rt.border }}>
                <span>{rt.label}</span>
                {rt.Icon && <rt.Icon size={10} />}
              </span>
            );
          })()}
          {(optionsMeta.showOptionsLabel || optionsMeta.showCustomizable) && (
            <div className="browse-item-card__meta-badges">
              {optionsMeta.showOptionsLabel && (
                <button
                  type="button"
                  className="browse-item-card__meta-badge"
                  onClick={() => optionsMeta.needsPicker && openOptionsSheet()}
                >
                  Options ({optionsMeta.sizeCount})
                </button>
              )}
              {optionsMeta.showCustomizable && (
                <button
                  type="button"
                  className="browse-item-card__meta-badge browse-item-card__meta-badge--custom"
                  onClick={() => optionsMeta.needsPicker && openOptionsSheet()}
                >
                  Customizable
                </button>
              )}
            </div>
          )}
          {!showImages && (
            <div className="browse-item-card__footer">
              {priceBlock}
              {controls}
            </div>
          )}
        </div>
      </article>

      <MenuItemOptionsSheet
        isOpen={cart.sheetOpen}
        item={item}
        appName={restaurant.appName}
        selections={selections}
        onChange={cart.setSelections}
        onClose={cart.closeSheet}
        onConfirm={cart.confirmSheet}
        formatPrice={formatPrice}
        error={cart.sheetError}
        busy={cart.sheetBusy}
        confirmLabel={quantity === 0 ? 'Add to cart' : 'Add item'}
      />
      <MenuLinePrompt
        prompt={cart.prompt}
        itemName={item.item}
        cartPath={`/restaurants/${restaurant.appName}/cart`}
        onClose={cart.closePrompt}
        onRepeat={cart.repeatLast}
        onChooseNew={cart.chooseNew}
      />
    </>
  );
};

export default MenuBrowseItemCard;
